import { useState, useEffect, useCallback, useRef } from 'react';
import {
  User, Package, MapPin, Heart, LogOut, ArrowLeft, ArrowRight,
  Pen, Plus, RotateCcw, FileText, Headphones, Check, Truck,
  Clock, Home, Building, ChevronDown, Eye, EyeOff, X,
  ShoppingBag, RefreshCw, Star, Printer, CheckCircle2, Loader2,
} from 'lucide-react';
import WishlistPage from '../WishlistPage/WishlistPage';

const cur = () => window.themeData?.currency ?? '$';

// ── API helpers ───────────────────────────────────────────────────────────────
function apiUrl(path) {
  const base = (window.themeData?.apiUrl ?? '').replace(/\/$/, '');
  return `${base}/little-anvi/v1${path}`;
}
const hdrs = () => ({
  'Content-Type': 'application/json',
  'X-WP-Nonce': window.themeData?.nonce ?? '',
});
async function apiFetch(path, opts = {}) {
  const res = await fetch(apiUrl(path), { headers: hdrs(), ...opts });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
}

// ── URL routing ───────────────────────────────────────────────────────────────
function getTab() {
  const p = window.location.pathname;
  const q = new URLSearchParams(window.location.search);
  if (p.includes('/lost-password')) {
    return (q.get('key') && q.get('login')) ? 'reset-password' : 'forgot-password';
  }
  if (p.includes('/view-order/')) {
    const m = p.match(/view-order\/(\d+)/);
    return m ? `order-${m[1]}` : 'orders';
  }
  if (p.includes('/orders'))        return 'orders';
  if (p.includes('/edit-account'))  return 'profile';
  if (p.includes('/edit-address'))  return 'addresses';
  if (p.includes('/wishlist'))      return 'wishlist';
  if (p.includes('/downloads'))     return 'orders';
  return 'dashboard';
}

function navTo(endpoint) {
  const base = window.themeData?.siteUrl ?? '';
  window.history.pushState({}, '', `${base}/my-account/${endpoint}`);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

// ── Status badge ──────────────────────────────────────────────────────────────
const STATUS_CFG = {
  completed:       { label: 'Delivered',  cls: 'bg-gray-100 text-textLight border-gray-200',          icon: <Check className="w-3 h-3" /> },
  processing:      { label: 'Processing', cls: 'bg-primary/10 text-primary border-primary/20',         icon: <RefreshCw className="w-3 h-3" /> },
  'on-hold':       { label: 'On Hold',    cls: 'bg-secondary/20 text-textDark border-secondary/30',   icon: <Clock className="w-3 h-3" /> },
  pending:         { label: 'Pending',    cls: 'bg-gray-100 text-textLight border-gray-200',           icon: <Clock className="w-3 h-3" /> },
  shipped:         { label: 'Shipped',    cls: 'bg-accent3/10 text-accent3 border-accent3/20',         icon: <Truck className="w-3 h-3" /> },
  cancelled:       { label: 'Cancelled',  cls: 'bg-red-50 text-red-500 border-red-100',                icon: <X className="w-3 h-3" /> },
  refunded:        { label: 'Refunded',   cls: 'bg-red-50 text-red-500 border-red-100',                icon: <X className="w-3 h-3" /> },
  'checkout-draft':{ label: 'Draft',      cls: 'bg-gray-100 text-textLight border-gray-200',           icon: null },
};
function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] ?? { label: status, cls: 'bg-gray-100 text-textLight border-gray-200', icon: null };
  return (
    <span className={`px-3 py-1 text-xs font-bold rounded-full border flex items-center gap-1 ${cfg.cls}`}>
      {cfg.icon}{cfg.label}
    </span>
  );
}

// ── Address form (inline modal) ───────────────────────────────────────────────
const ADDR_FIELDS = [
  { key: 'first_name', label: 'First Name',   half: true },
  { key: 'last_name',  label: 'Last Name',    half: true },
  { key: 'company',    label: 'Company',      half: false },
  { key: 'address_1',  label: 'Address',      half: false },
  { key: 'address_2',  label: 'Address 2',    half: false },
  { key: 'city',       label: 'City',         half: true },
  { key: 'state',      label: 'State',        half: true },
  { key: 'postcode',   label: 'ZIP / Postcode', half: true },
  { key: 'country',    label: 'Country',      half: true },
  { key: 'phone',      label: 'Phone',        half: true },
  { key: 'email',      label: 'Email',        half: true },
];

function AddressForm({ type, initial = {}, onSave, onCancel }) {
  const [form, setForm]   = useState({ ...initial });
  const [saving, setSaving] = useState(false);
  const [err, setErr]     = useState('');

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    setErr('');
    try {
      await apiFetch('/account/address', {
        method: 'POST',
        body: JSON.stringify({ type, ...form }),
      });
      onSave({ type, ...form });
    } catch (ex) {
      setErr('Failed to save. Please try again.');
    }
    setSaving(false);
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {ADDR_FIELDS.filter(f => f.key !== 'email' || type === 'billing').map(f => (
          <div key={f.key} className={f.half ? '' : 'md:col-span-2'}>
            <label className="block text-sm font-bold text-gray-700 mb-1">{f.label}</label>
            <input
              type="text"
              value={form[f.key] ?? ''}
              onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
              className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-textDark focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
            />
          </div>
        ))}
      </div>
      {err && <p className="text-red-500 text-sm font-bold">{err}</p>}
      <div className="flex gap-3 pt-2">
        <button type="submit" disabled={saving} className="bg-primary hover:bg-primaryHover text-white font-bold px-6 py-3 rounded-xl transition-colors disabled:opacity-60">
          {saving ? 'Saving…' : 'Save Address'}
        </button>
        <button type="button" onClick={onCancel} className="bg-gray-100 hover:bg-gray-200 text-textDark font-bold px-6 py-3 rounded-xl transition-colors">
          Cancel
        </button>
      </div>
    </form>
  );
}

// ── Sidebar ───────────────────────────────────────────────────────────────────
const NAV_ITEMS = [
  { id: 'dashboard', label: 'Profile',   icon: User    },
  { id: 'orders',    label: 'Orders',    icon: Package },
  { id: 'addresses', label: 'Addresses', icon: MapPin  },
  { id: 'wishlist',  label: 'Wishlist',  icon: Heart   },
];

function Sidebar({ user, activeTab, onNav }) {
  const initial   = user?.displayName?.[0]?.toUpperCase() ?? '?';
  const logoutUrl = window.themeData?.logoutUrl ?? `${window.themeData?.siteUrl ?? ''}/my-account/?action=logout`;

  const isActive = id =>
    activeTab === id ||
    (id === 'dashboard' && activeTab === 'profile') ||
    (id === 'orders' && activeTab.startsWith('order'));

  return (
    <aside className="w-full lg:w-64 flex-shrink-0">
      <div className="bg-white rounded-[32px] p-4 md:p-6 shadow-sm border border-gray-100 lg:sticky lg:top-28">
        {/* Avatar */}
        <div className="flex items-center gap-4 mb-6 lg:mb-8">
          <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-accent2/20 flex items-center justify-center border-4 border-white shadow-soft overflow-hidden flex-shrink-0">
            {user?.avatarUrl
              ? <img src={user.avatarUrl} alt={user.displayName} className="w-full h-full object-cover" />
              : <span className="font-black text-xl text-accent2">{initial}</span>
            }
          </div>
          <div className="min-w-0">
            <h2 className="font-black text-base md:text-lg text-textDark truncate">{user?.displayName ?? '…'}</h2>
            <p className="text-xs md:text-sm text-textLight font-bold truncate max-w-[150px]">{user?.email ?? ''}</p>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex flex-row lg:flex-col gap-2 overflow-x-auto lg:overflow-x-visible pb-4 lg:pb-0 hide-scrollbar snap-x">
          {NAV_ITEMS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => onNav(id)}
              className={`flex items-center gap-3 px-4 py-3 rounded-2xl font-bold transition-colors whitespace-nowrap snap-start text-left w-full ${isActive(id) ? 'bg-primary/10 text-primary' : 'hover:bg-gray-50 text-textLight hover:text-primary'}`}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              <span>{label}</span>
            </button>
          ))}
          <hr className="hidden lg:block border-gray-100 my-2" />
          <a href={logoutUrl} className="flex lg:hidden items-center gap-3 px-4 py-3 rounded-2xl hover:bg-red-50 text-red-500 font-bold transition-colors whitespace-nowrap snap-start">
            <LogOut className="w-5 h-5" /><span>Logout</span>
          </a>
          <a href={logoutUrl} className="hidden lg:flex items-center gap-3 px-4 py-3 rounded-2xl hover:bg-red-50 text-red-500 font-bold transition-colors mt-4">
            <LogOut className="w-5 h-5" /><span>Log Out</span>
          </a>
        </nav>
      </div>
    </aside>
  );
}

// ── Tab: Dashboard (Profile + Password + Addresses + Orders) ─────────────────
function TabDashboard({ user, setUser, orders, loading, onViewOrder, onNav }) {
  // Profile edit
  const [editProfile,   setEditProfile]   = useState(false);
  const [profileForm,   setProfileForm]   = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg,    setProfileMsg]    = useState('');

  // Password change
  const [showPwd,    setShowPwd]    = useState(false);
  const [pwdForm,    setPwdForm]    = useState({ current: '', newPwd: '', confirm: '' });
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [savingPwd,  setSavingPwd]  = useState(false);
  const [pwdMsg,     setPwdMsg]     = useState('');

  // Address editing
  const [editAddr, setEditAddr] = useState(null); // 'billing' | 'shipping' | null

  useEffect(() => {
    if (user) setProfileForm({ firstName: user.firstName ?? '', lastName: user.lastName ?? '', phone: user.phone ?? '' });
  }, [user]);

  async function saveProfile(e) {
    e.preventDefault();
    setSavingProfile(true);
    setProfileMsg('');
    try {
      await apiFetch('/account', { method: 'POST', body: JSON.stringify(profileForm) });
      setUser(u => ({ ...u, firstName: profileForm.firstName, lastName: profileForm.lastName, phone: profileForm.phone, displayName: `${profileForm.firstName} ${profileForm.lastName}`.trim() || u.displayName }));
      setEditProfile(false);
      setProfileMsg('Saved!');
      setTimeout(() => setProfileMsg(''), 3000);
    } catch { setProfileMsg('Failed to save.'); }
    setSavingProfile(false);
  }

  async function savePassword(e) {
    e.preventDefault();
    if (pwdForm.newPwd !== pwdForm.confirm) { setPwdMsg('Passwords do not match.'); return; }
    if (pwdForm.newPwd.length < 6) { setPwdMsg('Password must be at least 6 characters.'); return; }
    setSavingPwd(true);
    setPwdMsg('');
    try {
      await apiFetch('/account', { method: 'POST', body: JSON.stringify({ password: pwdForm.newPwd }) });
      setPwdForm({ current: '', newPwd: '', confirm: '' });
      setShowPwd(false);
      setPwdMsg('Password changed!');
      setTimeout(() => setPwdMsg(''), 3000);
    } catch { setPwdMsg('Failed to change password.'); }
    setSavingPwd(false);
  }

  function onAddressSaved(saved) {
    setUser(u => ({ ...u, [saved.type]: { ...u[saved.type], ...saved } }));
    setEditAddr(null);
  }

  const siteUrl = window.themeData?.siteUrl ?? '';

  return (
    <div className="space-y-6 md:space-y-8">

      {/* ── Profile Details ── */}
      <section className="bg-white rounded-[32px] p-6 md:p-8 shadow-sm border border-gray-100">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-textDark italic">Profile Details</h3>
          {!editProfile && (
            <button onClick={() => setEditProfile(true)} className="text-primary hover:text-primaryHover font-bold flex items-center gap-2 text-sm">
              <Pen className="w-4 h-4" /> Edit
            </button>
          )}
        </div>

        {editProfile ? (
          <form onSubmit={saveProfile} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {[['First Name','firstName'],['Last Name','lastName']].map(([lbl,k]) => (
                <div key={k}>
                  <label className="block text-sm font-bold text-gray-700 mb-2">{lbl}</label>
                  <input value={profileForm[k] ?? ''} onChange={e => setProfileForm(f => ({...f,[k]:e.target.value}))}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all" />
                </div>
              ))}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Email</label>
                <div className="bg-gray-50 rounded-xl px-4 py-3 text-gray-400 border border-gray-100 text-sm">{user?.email} <span className="text-xs">(read-only)</span></div>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Phone</label>
                <input type="tel" value={profileForm.phone ?? ''} onChange={e => setProfileForm(f => ({...f,phone:e.target.value}))}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all" />
              </div>
            </div>
            <div className="flex gap-3 items-center">
              <button type="submit" disabled={savingProfile} className="bg-primary hover:bg-primaryHover text-white font-bold px-6 py-3 rounded-xl transition-colors disabled:opacity-60">
                {savingProfile ? 'Saving…' : 'Save Changes'}
              </button>
              <button type="button" onClick={() => setEditProfile(false)} className="bg-gray-100 hover:bg-gray-200 text-textDark font-bold px-6 py-3 rounded-xl transition-colors">
                Cancel
              </button>
              {profileMsg && <span className="text-sm font-bold text-accent3">{profileMsg}</span>}
            </div>
          </form>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[['First Name',user?.firstName],['Last Name',user?.lastName],['Email Address',user?.email],['Phone Number',user?.phone||'—']].map(([lbl,val]) => (
              <div key={lbl}>
                <label className="block text-sm font-bold text-textLight mb-2">{lbl}</label>
                <div className="bg-gray-50 rounded-xl px-4 py-3 text-textDark font-medium border border-gray-100">{val ?? '—'}</div>
              </div>
            ))}
            {profileMsg && <p className="md:col-span-2 text-sm font-bold text-accent3">{profileMsg}</p>}
          </div>
        )}
      </section>

      {/* ── Change Password ── */}
      <section className="bg-white rounded-[32px] p-6 md:p-8 shadow-sm border border-gray-100">
        <div className="flex justify-between items-center">
          <h3 className="text-xl md:text-2xl font-bold text-textDark italic">Change Password</h3>
          <button onClick={() => setShowPwd(s => !s)} className="text-primary hover:text-primaryHover font-bold flex items-center gap-2 text-sm">
            {showPwd ? <X className="w-4 h-4" /> : <Pen className="w-4 h-4" />}
            {showPwd ? 'Cancel' : 'Change'}
          </button>
        </div>

        {showPwd && (
          <form onSubmit={savePassword} className="mt-6 space-y-4">
            {[['New Password','newPwd'],['Confirm Password','confirm']].map(([lbl,k]) => (
              <div key={k} className="relative">
                <label className="block text-sm font-bold text-gray-700 mb-2">{lbl}</label>
                <input
                  type={showNewPwd ? 'text' : 'password'}
                  value={pwdForm[k]}
                  onChange={e => setPwdForm(f => ({...f,[k]:e.target.value}))}
                  placeholder="••••••••"
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 pr-12 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                />
                {k === 'newPwd' && (
                  <button type="button" onClick={() => setShowNewPwd(s => !s)} className="absolute right-4 top-10 text-gray-400">
                    {showNewPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                )}
              </div>
            ))}
            <div className="flex gap-3 items-center">
              <button type="submit" disabled={savingPwd} className="bg-primary hover:bg-primaryHover text-white font-bold px-6 py-3 rounded-xl transition-colors disabled:opacity-60">
                {savingPwd ? 'Saving…' : 'Update Password'}
              </button>
              {pwdMsg && <span className={`text-sm font-bold ${pwdMsg.includes('!') ? 'text-accent3' : 'text-red-500'}`}>{pwdMsg}</span>}
            </div>
          </form>
        )}
        {!showPwd && <p className="mt-3 text-sm text-textLight font-bold">•••••••••• (hidden)</p>}
      </section>

      {/* ── Saved Addresses ── */}
      <section className="bg-white rounded-[32px] p-6 md:p-8 shadow-sm border border-gray-100">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-textDark italic">Saved Addresses</h3>
          {!editAddr && (
            <button onClick={() => setEditAddr('shipping')} className="text-primary hover:text-primaryHover font-bold flex items-center gap-2 text-sm">
              <Plus className="w-4 h-4" /> Add New
            </button>
          )}
        </div>

        {editAddr ? (
          <div>
            <h4 className="font-bold text-textDark mb-4 capitalize">{editAddr === 'billing' ? 'Billing' : 'Shipping'} Address</h4>
            <AddressForm
              type={editAddr}
              initial={user?.[editAddr] ?? {}}
              onSave={onAddressSaved}
              onCancel={() => setEditAddr(null)}
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <AddressCard
              type="Billing" icon={<Home className="w-5 h-5" />} iconCls="bg-primary/10 text-primary"
              isDefault data={user?.billing ?? {}}
              onEdit={() => setEditAddr('billing')}
            />
            <AddressCard
              type="Shipping" icon={<Truck className="w-5 h-5" />} iconCls="bg-accent2/10 text-accent2"
              isDefault={false} data={user?.shipping ?? {}}
              onEdit={() => setEditAddr('shipping')}
            />
          </div>
        )}
      </section>

      {/* ── Recent Orders ── */}
      <section className="bg-white rounded-[32px] p-6 md:p-8 shadow-sm border border-gray-100">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl md:text-2xl font-bold text-textDark italic">Recent Orders</h3>
          <button onClick={() => onNav('orders')} className="text-primary hover:text-primaryHover font-bold flex items-center gap-2 text-sm">
            View All <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        {loading ? (
          <div className="space-y-4">{[1,2].map(i=><div key={i} className="h-24 bg-gray-100 rounded-2xl animate-pulse"/>)}</div>
        ) : orders.length === 0 ? (
          <EmptyOrders siteUrl={window.themeData?.siteUrl ?? ''} />
        ) : (
          <div className="space-y-4 md:space-y-5">
            {orders.slice(0,3).map(o => <OrderRow key={o.id} order={o} onView={() => onViewOrder(o.id)} />)}
          </div>
        )}
      </section>
    </div>
  );
}

// ── Tab: Orders list ──────────────────────────────────────────────────────────
function TabOrders({ orders, loading, onViewOrder }) {
  return (
    <div className="bg-white rounded-[32px] p-6 md:p-8 shadow-sm border border-gray-100">
      <h3 className="text-2xl font-bold text-textDark italic mb-6">My Orders</h3>
      {loading ? (
        <div className="space-y-4">{[1,2,3].map(i=><div key={i} className="h-24 bg-gray-100 rounded-2xl animate-pulse"/>)}</div>
      ) : orders.length === 0 ? (
        <EmptyOrders siteUrl={window.themeData?.siteUrl ?? ''} />
      ) : (
        <div className="space-y-4 md:space-y-5">
          {orders.map(o => <OrderRow key={o.id} order={o} onView={() => onViewOrder(o.id)} showBuyAgain />)}
        </div>
      )}
    </div>
  );
}

// ── Tab: Addresses (standalone) ───────────────────────────────────────────────
function TabAddresses({ user, setUser }) {
  const [editAddr, setEditAddr] = useState(null);

  function onSaved(saved) {
    setUser(u => ({ ...u, [saved.type]: { ...u[saved.type], ...saved } }));
    setEditAddr(null);
  }

  return (
    <div className="bg-white rounded-[32px] p-6 md:p-8 shadow-sm border border-gray-100">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-2xl font-bold text-textDark italic">Saved Addresses</h3>
        {!editAddr && (
          <button onClick={() => setEditAddr('shipping')} className="text-primary font-bold flex items-center gap-2 text-sm hover:text-primaryHover">
            <Plus className="w-4 h-4" /> Add New
          </button>
        )}
      </div>

      {editAddr ? (
        <div>
          <h4 className="font-bold text-textDark mb-4 capitalize">{editAddr} Address</h4>
          <AddressForm type={editAddr} initial={user?.[editAddr] ?? {}} onSave={onSaved} onCancel={() => setEditAddr(null)} />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <AddressCard type="Billing" icon={<Home className="w-5 h-5" />} iconCls="bg-primary/10 text-primary" isDefault data={user?.billing ?? {}} onEdit={() => setEditAddr('billing')} />
          <AddressCard type="Shipping" icon={<Truck className="w-5 h-5" />} iconCls="bg-accent2/10 text-accent2" isDefault={false} data={user?.shipping ?? {}} onEdit={() => setEditAddr('shipping')} />
        </div>
      )}
    </div>
  );
}

// ── Tab: Wishlist ─────────────────────────────────────────────────────────────
function TabWishlist() {
  return <WishlistPage />;
}

// ── Invoice Modal ─────────────────────────────────────────────────────────────
function InvoiceModal({ order, onClose }) {
  const printRef = useRef();
  const shopName  = window.themeData?.blogName  ?? 'Little Anvi';
  const siteUrl   = window.themeData?.siteUrl   ?? '';
  const currency  = window.themeData?.currency  ?? '₹';

  function handlePrint() {
    const content = printRef.current?.innerHTML ?? '';
    const win = window.open('', '_blank', 'width=900,height=750,scrollbars=yes');
    win.document.write(`<!DOCTYPE html><html><head>
      <meta charset="utf-8" />
      <title>Invoice #${order.number}</title>
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: system-ui, -apple-system, sans-serif; color: #1f2937; background: #fff; padding: 40px; font-size: 14px; }
        .inv-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; padding-bottom: 24px; border-bottom: 2px solid #e5e7eb; }
        .inv-brand { font-size: 28px; font-weight: 900; color: #8B5CF6; }
        .inv-brand small { display: block; font-size: 12px; font-weight: 500; color: #6b7280; margin-top: 4px; }
        .inv-meta { text-align: right; }
        .inv-meta h1 { font-size: 22px; font-weight: 900; color: #1f2937; }
        .inv-meta p { font-size: 13px; color: #6b7280; margin-top: 4px; }
        .inv-badge { display: inline-block; background: #f3f4f6; border-radius: 9999px; padding: 3px 12px; font-size: 12px; font-weight: 700; color: #374151; margin-top: 6px; text-transform: capitalize; }
        .inv-addresses { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 32px; }
        .inv-addr-box { background: #f9fafb; border-radius: 12px; padding: 20px; }
        .inv-addr-box h4 { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #8B5CF6; margin-bottom: 10px; }
        .inv-addr-box p { font-size: 13px; color: #374151; line-height: 1.7; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
        thead th { text-align: left; padding: 10px 12px; background: #8B5CF6; color: #fff; font-size: 12px; font-weight: 700; text-transform: uppercase; }
        thead th:last-child { text-align: right; }
        tbody tr { border-bottom: 1px solid #f3f4f6; }
        tbody td { padding: 12px; font-size: 13px; color: #374151; vertical-align: middle; }
        tbody td:last-child { text-align: right; font-weight: 700; }
        .item-img { width: 40px; height: 40px; object-fit: cover; border-radius: 6px; display: inline-block; vertical-align: middle; margin-right: 10px; }
        .inv-totals { margin-left: auto; width: 280px; }
        .inv-totals tr td { padding: 7px 12px; }
        .inv-totals .total-row td { font-size: 16px; font-weight: 900; color: #8B5CF6; border-top: 2px solid #e5e7eb; padding-top: 12px; }
        .inv-footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; font-size: 12px; color: #9ca3af; }
        @media print { body { padding: 20px; } }
      </style>
    </head><body>${content}</body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 400);
  }

  const billing  = order.billing          ?? {};
  const shipping = order.shipping_address ?? {};
  const addrLine = (a) => [a.address_1, a.address_2, a.city, a.state, a.postcode, a.country].filter(Boolean).join(', ');

  return (
    <div className="fixed inset-0 bg-black/60 z-[150] flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-[32px] w-full max-w-3xl shadow-2xl">
        {/* Toolbar */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-gray-100 no-print">
          <h2 className="text-xl font-black text-textDark">Invoice #{order.number}</h2>
          <div className="flex items-center gap-3">
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 bg-primary hover:bg-primaryHover text-white font-bold px-5 py-2.5 rounded-xl transition-colors text-sm"
            >
              <Printer className="w-4 h-4" /> Print / Save PDF
            </button>
            <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-500 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Invoice content */}
        <div className="overflow-y-auto max-h-[75vh] p-8" ref={printRef}>
          {/* Header */}
          <div className="inv-header flex justify-between items-start mb-10 pb-6 border-b-2 border-gray-100">
            <div>
              <div className="inv-brand text-3xl font-black text-primary">{shopName}</div>
              <div className="text-xs text-gray-400 font-bold mt-1">{siteUrl}</div>
            </div>
            <div className="text-right">
              <h1 className="text-2xl font-black text-textDark">INVOICE</h1>
              <p className="text-sm text-gray-500 font-bold mt-1">#{order.number}</p>
              <p className="text-sm text-gray-500 mt-0.5">Date: {order.date}</p>
              <span className="inline-block mt-2 bg-gray-100 text-gray-600 text-xs font-bold px-3 py-1 rounded-full capitalize">
                {order.status}
              </span>
            </div>
          </div>

          {/* Addresses */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
            {billing.address_1 && (
              <div className="bg-gray-50 rounded-2xl p-5">
                <h4 className="text-xs font-black text-primary uppercase tracking-wider mb-3">Bill To</h4>
                <p className="text-sm text-textDark font-bold">{billing.first_name} {billing.last_name}</p>
                {billing.company && <p className="text-sm text-gray-500">{billing.company}</p>}
                <p className="text-sm text-gray-500 mt-1">{addrLine(billing)}</p>
                {billing.email && <p className="text-sm text-gray-500">{billing.email}</p>}
                {billing.phone && <p className="text-sm text-gray-500">{billing.phone}</p>}
              </div>
            )}
            {shipping.address_1 && (
              <div className="bg-gray-50 rounded-2xl p-5">
                <h4 className="text-xs font-black text-primary uppercase tracking-wider mb-3">Ship To</h4>
                <p className="text-sm text-textDark font-bold">{shipping.first_name} {shipping.last_name}</p>
                <p className="text-sm text-gray-500 mt-1">{addrLine(shipping)}</p>
              </div>
            )}
          </div>

          {/* Items table */}
          <div className="overflow-x-auto mb-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-primary text-white">
                  <th className="text-left px-4 py-3 rounded-tl-xl text-xs font-black uppercase tracking-wide">Item</th>
                  <th className="text-center px-4 py-3 text-xs font-black uppercase tracking-wide">Qty</th>
                  <th className="text-right px-4 py-3 rounded-tr-xl text-xs font-black uppercase tracking-wide">Total</th>
                </tr>
              </thead>
              <tbody>
                {(order.items ?? []).map((item, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="px-4 py-3 flex items-center gap-3">
                      {item.image && (
                        <img src={item.image} alt={item.name} className="w-10 h-10 object-cover rounded-lg flex-shrink-0 border border-gray-100" />
                      )}
                      <div>
                        <p className="font-bold text-textDark">{item.name}</p>
                        {item.sku && <p className="text-xs text-gray-400 mt-0.5">SKU: {item.sku}</p>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-600 font-bold">{item.qty}</td>
                    <td className="px-4 py-3 text-right font-black text-textDark">{currency}{item.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end mb-8">
            <div className="w-64 space-y-2 text-sm font-bold">
              <div className="flex justify-between text-gray-500">
                <span>Subtotal</span><span className="text-textDark">{currency}{order.subtotal}</span>
              </div>
              <div className="flex justify-between text-gray-500">
                <span>Shipping</span>
                <span className={parseFloat(order.shipping) === 0 ? 'text-green-600' : 'text-textDark'}>
                  {parseFloat(order.shipping) === 0 ? 'FREE' : `${currency}${order.shipping}`}
                </span>
              </div>
              {parseFloat(order.discount) > 0 && (
                <div className="flex justify-between text-gray-500">
                  <span>Discount{order.couponCodes?.[0] ? ` (${order.couponCodes[0]})` : ''}</span>
                  <span className="text-green-600">-{currency}{order.discount}</span>
                </div>
              )}
              <div className="border-t border-gray-200 pt-3 flex justify-between text-lg font-black text-primary">
                <span>Total</span><span>{currency}{order.total}</span>
              </div>
              {order.paymentMethod && (
                <p className="text-xs text-gray-400 text-right pt-1">Paid via {order.paymentMethod}</p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-100 pt-6 text-center text-xs text-gray-400 font-bold">
            <p>Thank you for shopping with {shopName}!</p>
            <p className="mt-1">{siteUrl}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Tab: Order detail + tracking ─────────────────────────────────────────────
const TRACKING_STEPS = [
  { statuses: ['pending','processing','on-hold','shipped','completed'], label: 'Order Placed',     icon: <Check className="w-3 h-3 md:w-4 md:h-4" /> },
  { statuses: ['processing','on-hold','shipped','completed'],           label: 'Order Packed',     icon: <Package className="w-3 h-3 md:w-4 md:h-4" /> },
  { statuses: ['shipped','completed'],                                   label: 'Out for Delivery', icon: <Truck className="w-3 h-3 md:w-4 md:h-4" /> },
  { statuses: ['completed'],                                             label: 'Delivered',        icon: <Home className="w-3 h-3 md:w-4 md:h-4" /> },
];

function TrackingTimeline({ status }) {
  const doneCount = TRACKING_STEPS.filter(s => s.statuses.includes(status)).length;
  const pct = TRACKING_STEPS.length > 1 ? ((doneCount - 1) / (TRACKING_STEPS.length - 1)) * 100 : 0;

  return (
    <div className="relative pl-12 md:pl-20">
      <div className="absolute left-5 md:left-9 top-2 bottom-2 w-0.5 bg-gray-200" />
      <div className="absolute left-5 md:left-9 top-2 w-0.5 bg-accent3 transition-all duration-500" style={{ height: `${Math.max(0, pct)}%` }} />
      <div className="space-y-10 md:space-y-12">
        {TRACKING_STEPS.map((step, i) => {
          const done    = step.statuses.includes(status);
          const current = done && i === doneCount - 1;
          return (
            <div key={i} className={`relative z-10 ${!done ? 'opacity-50' : ''}`}>
              <div className={`absolute -left-12 md:-left-20 w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center border-4 border-white shadow-sm ${current ? 'bg-secondary animate-pulse text-textDark' : done ? 'bg-accent3 text-white' : 'bg-gray-200 text-gray-500'}`}>
                {step.icon}
              </div>
              <div>
                <h4 className={`font-bold text-sm md:text-base ${current ? 'text-textDark text-base md:text-lg' : 'text-textDark'}`}>{step.label}</h4>
                <p className="text-xs md:text-sm text-textLight mt-1">{done ? 'Completed' : 'Pending'}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TabOrderDetail({ orderId, onBack }) {
  const [order,       setOrder]       = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [showInvoice, setShowInvoice] = useState(false);
  const [reordering,  setReordering]  = useState(false);
  const [reorderMsg,  setReorderMsg]  = useState('');

  useEffect(() => {
    setLoading(true);
    apiFetch(`/account/orders/${orderId}`)
      .then(setOrder).catch(() => setOrder(null)).finally(() => setLoading(false));
  }, [orderId]);

  async function handleReorder() {
    if (!order?.items?.length) return;
    setReordering(true);
    setReorderMsg('');
    const wcBase  = (window.themeData?.apiUrl ?? '').replace(/\/$/, '') + '/wc/store/v1';
    const wcNonce = window.themeData?.wcStoreNonce ?? '';
    try {
      for (const item of order.items) {
        await fetch(`${wcBase}/cart/add-item`, {
          method:  'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Nonce': wcNonce },
          body: JSON.stringify({ id: item.cart_id ?? item.id, quantity: item.qty }),
        });
      }
      window.dispatchEvent(new CustomEvent('anvi:cart-update'));
      setReorderMsg('Added! Redirecting to cart…');
      setTimeout(() => {
        window.location.href = `${window.themeData?.siteUrl ?? ''}/cart/`;
      }, 1200);
    } catch (_) {
      setReorderMsg('Failed to add some items. Please try again.');
    }
    setReordering(false);
  }

  if (loading) return (
    <div className="space-y-4">
      <div className="h-12 bg-gray-100 rounded-2xl animate-pulse w-1/3" />
      <div className="h-64 bg-gray-100 rounded-[32px] animate-pulse" />
    </div>
  );

  if (!order) return (
    <div className="bg-white rounded-[32px] p-12 text-center shadow-sm border border-gray-100">
      <p className="text-textLight font-bold mb-4">Order not found.</p>
      <button onClick={onBack} className="text-primary font-bold hover:underline">← Back to orders</button>
    </div>
  );

  const siteUrl = window.themeData?.siteUrl ?? '';

  return (
    <div className="space-y-6 md:space-y-8">
      {showInvoice && order && (
        <InvoiceModal order={order} onClose={() => setShowInvoice(false)} />
      )}
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center text-textDark hover:bg-gray-50 transition-colors shadow-sm">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-textDark italic">Order Details</h1>
            <p className="text-sm font-bold text-textLight mt-0.5">Order <span className="text-primary">#{order.number}</span> · {order.date}</p>
          </div>
        </div>
        <StatusBadge status={order.status} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 md:gap-8">
        {/* Left: Tracking + Items */}
        <div className="xl:col-span-2 space-y-6 md:space-y-8">
          <section className="bg-white rounded-[32px] p-6 md:p-8 shadow-sm border border-gray-100">
            <h3 className="text-xl md:text-2xl font-bold text-textDark mb-8 italic">Tracking Status</h3>
            <TrackingTimeline status={order.status} />
          </section>

          <section className="bg-white rounded-[32px] p-6 md:p-8 shadow-sm border border-gray-100">
            <h3 className="text-xl md:text-2xl font-bold text-textDark mb-6 italic">
              Items ({order.items?.length ?? 0})
            </h3>
            <div className="space-y-4">
              {(order.items ?? []).map((item, i) => (
                <div key={i} className="flex flex-col sm:flex-row gap-4 p-4 border border-gray-100 rounded-2xl hover:border-primary/30 transition-colors">
                  <div className="w-full sm:w-24 h-24 bg-gray-50 rounded-xl p-2 flex-shrink-0 flex items-center justify-center">
                    {item.image ? <img src={item.image} alt={item.name} className="w-full h-full object-contain" /> : <span className="text-4xl">🎁</span>}
                  </div>
                  <div className="flex-grow flex flex-col justify-between">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-bold text-textDark text-sm md:text-base">{item.name}</h4>
                        {item.category && <p className="text-xs text-textLight mt-1">{item.category}</p>}
                      </div>
                      <p className="font-black text-textDark">{cur()}{item.total}</p>
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-sm font-bold text-textLight bg-gray-100 px-3 py-1 rounded-lg">Qty: {item.qty}</span>
                      {order.status === 'completed' && (
                        <button className="text-primary hover:text-primaryHover text-sm font-bold flex items-center gap-1">
                          <Star className="w-3 h-3" /> Write Review
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Right: Actions + Summary */}
        <div className="space-y-6 md:space-y-8">
          <section className="bg-white rounded-[32px] p-6 md:p-8 shadow-sm border border-gray-100">
            <h3 className="text-xl font-bold text-textDark mb-6 italic">Quick Actions</h3>
            <div className="space-y-3">
              <button
                onClick={handleReorder}
                disabled={reordering}
                className="w-full py-3 px-4 bg-primary text-white rounded-xl font-bold hover:bg-primaryHover transition-colors shadow-soft flex justify-center items-center gap-2 disabled:opacity-60"
              >
                {reordering
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Adding to Cart…</>
                  : <><RotateCcw className="w-4 h-4" /> Reorder All</>
                }
              </button>
              {reorderMsg && (
                <p className={`text-xs font-bold px-3 py-2 rounded-xl text-center ${reorderMsg.includes('Failed') ? 'bg-red-50 text-red-500' : 'bg-accent3/10 text-accent3'}`}>
                  {reorderMsg}
                </p>
              )}
              <button
                onClick={() => setShowInvoice(true)}
                className="w-full py-3 px-4 bg-white border-2 border-primary text-primary rounded-xl font-bold hover:bg-primary/5 transition-colors flex justify-center items-center gap-2"
              >
                <FileText className="w-4 h-4" /> View Invoice
              </button>
              <a href={`${siteUrl}/contact/`} className="w-full py-3 px-4 bg-gray-50 text-textDark rounded-xl font-bold hover:bg-gray-100 transition-colors border border-gray-200 flex justify-center items-center gap-2">
                <Headphones className="w-4 h-4" /> Contact Support
              </a>
            </div>
          </section>

          <section className="bg-white rounded-[32px] p-6 md:p-8 shadow-sm border border-gray-100">
            <h3 className="text-xl font-bold text-textDark mb-6 italic">Payment Summary</h3>
            <div className="space-y-3 text-sm font-bold text-textLight">
              <div className="flex justify-between"><span>Subtotal</span><span className="text-textDark">{cur()}{order.subtotal}</span></div>
              <div className="flex justify-between">
                <span>Shipping</span>
                <span className={parseFloat(order.shipping) === 0 ? 'text-accent3' : 'text-textDark'}>
                  {parseFloat(order.shipping) === 0 ? 'FREE' : `${cur()}${order.shipping}`}
                </span>
              </div>
              {parseFloat(order.discount) > 0 && (
                <div className="flex justify-between">
                  <span>Discount {order.couponCodes?.[0] && `(${order.couponCodes[0]})`}</span>
                  <span className="text-accent3">-{cur()}{order.discount}</span>
                </div>
              )}
              <hr className="border-gray-100" />
              <div className="flex justify-between text-lg font-black text-textDark pt-1">
                <span>Total</span><span>{cur()}{order.total}</span>
              </div>
            </div>
            {order.paymentMethod && (
              <div className="mt-4 p-4 bg-gray-50 rounded-2xl flex items-center gap-3 border border-gray-100">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm text-primary border border-gray-200 text-xl">💳</div>
                <div>
                  <p className="text-xs text-textLight font-bold">Payment Method</p>
                  <p className="font-bold text-textDark text-sm">{order.paymentMethod}</p>
                </div>
              </div>
            )}
          </section>

          {order.shipping_address?.address_1 && (
            <section className="bg-white rounded-[32px] p-6 md:p-8 shadow-sm border border-gray-100">
              <h3 className="text-xl font-bold text-textDark mb-4 italic">Shipping Address</h3>
              <div className="text-sm text-textLight space-y-1 font-bold">
                <p className="text-textDark font-black">{order.shipping_address.first_name} {order.shipping_address.last_name}</p>
                {order.shipping_address.address_1 && <p>{order.shipping_address.address_1}</p>}
                {order.shipping_address.address_2 && <p>{order.shipping_address.address_2}</p>}
                <p>{[order.shipping_address.city, order.shipping_address.state, order.shipping_address.postcode].filter(Boolean).join(', ')}</p>
                {order.shipping_address.country && <p>{order.shipping_address.country}</p>}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Shared helpers ────────────────────────────────────────────────────────────
function AddressCard({ type, icon, iconCls, isDefault, data, onEdit }) {
  const name  = [data?.first_name, data?.last_name].filter(Boolean).join(' ');
  const lines = [data?.address_1, data?.address_2, [data?.city, data?.state, data?.postcode].filter(Boolean).join(', '), data?.country].filter(Boolean);
  const empty = lines.length === 0;

  return (
    <div className={`bg-gray-50 rounded-2xl p-6 border-2 relative overflow-hidden ${isDefault ? 'border-primary' : 'border-gray-100 hover:border-primary/30 transition-colors'}`}>
      {isDefault && <div className="absolute top-0 right-0 bg-primary text-white text-xs font-bold px-3 py-1 rounded-bl-xl">Default</div>}
      <div className="flex items-start gap-4 mb-4">
        <div className={`w-10 h-10 rounded-full ${iconCls} flex items-center justify-center flex-shrink-0`}>{icon}</div>
        <div>
          <h4 className="font-bold text-textDark">{type}</h4>
          {empty ? (
            <p className="text-sm text-textLight mt-1">No address saved</p>
          ) : (
            <>
              {name && <p className="text-sm text-textLight mt-1 font-semibold">{name}</p>}
              {lines.map((l, i) => <p key={i} className="text-sm text-textLight">{l}</p>)}
            </>
          )}
        </div>
      </div>
      <div className="flex gap-4 mt-4 pt-4 border-t border-gray-200">
        <button onClick={onEdit} className="text-sm font-bold text-primary hover:text-primaryHover">
          {empty ? 'Add Address' : 'Edit'}
        </button>
      </div>
    </div>
  );
}

function OrderRow({ order, onView, showBuyAgain = false }) {
  return (
    <div className="border border-gray-100 rounded-2xl p-4 md:p-6 hover:shadow-md transition-shadow">
      <div className="flex flex-col md:flex-row justify-between md:items-center gap-4 mb-4 border-b border-gray-100 pb-4">
        <div>
          <p className="text-xs md:text-sm font-bold text-textLight">Order <span className="text-primary">#{order.number}</span></p>
          <p className="text-xs md:text-sm text-textLight mt-1">Placed on {order.date}</p>
        </div>
        <div className="flex items-center justify-between md:justify-end gap-4">
          <StatusBadge status={order.status} />
          <p className="font-black text-base md:text-lg text-textDark">{cur()}{order.total}</p>
        </div>
      </div>
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 md:gap-6">
        <div className="flex items-center gap-4 w-full md:w-auto">
          <div className="flex -space-x-3 md:-space-x-4">
            {(order.items ?? []).slice(0,3).map((item, i) => (
              <div key={i} className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gray-50 border-2 border-white p-1 shadow-sm overflow-hidden flex items-center justify-center">
                {item.image ? <img src={item.image} alt={item.name} className="w-full h-full object-contain" /> : <span className="text-lg">🎁</span>}
              </div>
            ))}
          </div>
          <p className="text-sm font-bold text-textDark">{order.itemCount} Item{order.itemCount !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex gap-3 w-full md:w-auto">
          {showBuyAgain && order.status === 'completed' && (
            <button className="flex-1 md:flex-none px-4 py-2 border border-gray-200 rounded-xl text-sm font-bold text-textDark hover:bg-gray-50 transition-colors">Buy Again</button>
          )}
          {!['completed','cancelled','refunded'].includes(order.status) && (
            <button onClick={onView} className="flex-1 md:flex-none px-4 py-2 border border-gray-200 rounded-xl text-sm font-bold text-textDark hover:bg-gray-50 transition-colors">Track</button>
          )}
          <button onClick={onView} className="flex-1 md:flex-none px-4 py-2 bg-primary/10 text-primary rounded-xl text-sm font-bold hover:bg-primary hover:text-white transition-colors">Details</button>
        </div>
      </div>
    </div>
  );
}

function EmptyOrders({ siteUrl }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="w-24 h-24 bg-secondary/20 rounded-full flex items-center justify-center mb-6">
        <ShoppingBag className="w-10 h-10 text-secondary" />
      </div>
      <h4 className="text-2xl font-bold text-textDark mb-2 italic">No Orders Yet!</h4>
      <p className="text-textLight mb-6 max-w-md">Looks like you haven't placed any orders yet. Start exploring!</p>
      <a href={`${siteUrl}/shop/`} className="bg-primary hover:bg-primaryHover text-white font-bold py-3 px-8 rounded-full shadow-soft transition-all">
        Start Shopping
      </a>
    </div>
  );
}

// ── Forgot password ───────────────────────────────────────────────────────────
function TabForgotPassword() {
  const [email,   setEmail]   = useState('');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [sent,    setSent]    = useState(false);
  const siteUrl = window.themeData?.siteUrl ?? '';

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res  = await fetch(apiUrl('/auth/forgot-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed. Please try again.'); return; }
      setSent(true);
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="max-w-md mx-auto py-12">
      <div className="bg-white rounded-[32px] p-8 shadow-sm border border-gray-100">
        <a href={`${siteUrl}/my-account/`} className="flex items-center gap-2 text-sm font-bold text-textLight hover:text-primary mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Sign In
        </a>
        <h2 className="text-2xl font-black text-textDark mb-1 italic">Forgot Password?</h2>
        <p className="text-sm font-bold text-textLight mb-6">Enter your email and we'll send you a reset link.</p>

        {sent ? (
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-accent3/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-accent3" />
            </div>
            <h3 className="text-lg font-black text-textDark mb-2">Check your inbox!</h3>
            <p className="text-sm font-bold text-textLight">If an account exists for <span className="text-textDark">{email}</span>, you'll receive a reset link shortly.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="you@example.com"
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all text-textDark"
              />
            </div>
            {error && <p className="text-red-500 text-sm font-bold bg-red-50 px-4 py-3 rounded-xl">{error}</p>}
            <button type="submit" disabled={loading} className="w-full bg-primary hover:bg-primaryHover text-white font-black py-4 rounded-2xl transition-colors disabled:opacity-60 text-sm">
              {loading ? 'Sending…' : 'Send Reset Link'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Reset password (from email link) ──────────────────────────────────────────
function TabResetPassword({ resetKey, login }) {
  const [password, setPassword] = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [showPwd,  setShowPwd]  = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [done,     setDone]     = useState(false);
  const siteUrl = window.themeData?.siteUrl ?? '';

  async function handleSubmit(e) {
    e.preventDefault();
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (password.length < 6)  { setError('Password must be at least 6 characters.'); return; }
    setLoading(true);
    setError('');
    try {
      const res  = await fetch(apiUrl('/auth/reset-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: resetKey, login, password }),
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed. Please try again.'); return; }
      setDone(true);
      setTimeout(() => { window.location.href = `${siteUrl}/my-account/`; }, 2500);
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  if (!resetKey || !login) {
    return (
      <div className="max-w-md mx-auto py-12 text-center">
        <p className="text-red-500 font-bold mb-4">Invalid or missing reset link.</p>
        <a href={`${siteUrl}/my-account/lost-password/`} className="text-primary font-bold hover:text-primaryHover">
          Request a new reset link →
        </a>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto py-12">
      <div className="bg-white rounded-[32px] p-8 shadow-sm border border-gray-100">
        <h2 className="text-2xl font-black text-textDark mb-1 italic">Set New Password</h2>
        <p className="text-sm font-bold text-textLight mb-6">Choose a strong password for your account.</p>

        {done ? (
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-accent3/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-accent3" />
            </div>
            <h3 className="text-lg font-black text-textDark mb-2">Password Updated!</h3>
            <p className="text-sm font-bold text-textLight">Signing you in and redirecting…</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <label className="block text-sm font-bold text-gray-700 mb-2">New Password</label>
              <input
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                minLength={6}
                autoComplete="new-password"
                placeholder="Min. 6 characters"
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 pr-12 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all text-textDark"
              />
              <button type="button" onClick={() => setShowPwd(s => !s)} className="absolute right-4 top-10 text-gray-400 hover:text-gray-600">
                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Confirm Password</label>
              <input
                type="password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                required
                autoComplete="new-password"
                placeholder="••••••••"
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all text-textDark"
              />
            </div>
            {error && <p className="text-red-500 text-sm font-bold bg-red-50 px-4 py-3 rounded-xl">{error}</p>}
            <button type="submit" disabled={loading} className="w-full bg-primary hover:bg-primaryHover text-white font-black py-4 rounded-2xl transition-colors disabled:opacity-60 text-sm">
              {loading ? 'Saving…' : 'Save New Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Login / Register panel (shown when not logged in) ─────────────────────────
function LoginRegisterPanel() {
  const [mode, setMode] = useState('login');

  const [loginForm, setLoginForm]       = useState({ username: '', password: '', remember: false });
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError]     = useState('');
  const [showLoginPwd, setShowLoginPwd] = useState(false);

  const [regForm, setRegForm]           = useState({ firstName: '', lastName: '', email: '', password: '' });
  const [regLoading, setRegLoading]     = useState(false);
  const [regError, setRegError]         = useState('');
  const [showRegPwd, setShowRegPwd]     = useState(false);

  const siteUrl = window.themeData?.siteUrl ?? '';

  async function handleLogin(e) {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      const res  = await fetch(apiUrl('/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm),
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) { setLoginError(data.error || 'Login failed. Please try again.'); return; }
      window.location.reload();
    } catch {
      setLoginError('Connection error. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  }

  async function handleRegister(e) {
    e.preventDefault();
    if (regForm.password.length < 6) { setRegError('Password must be at least 6 characters.'); return; }
    setRegLoading(true);
    setRegError('');
    try {
      const res  = await fetch(apiUrl('/auth/register'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(regForm),
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) { setRegError(data.error || 'Registration failed. Please try again.'); return; }
      window.location.reload();
    } catch {
      setRegError('Connection error. Please try again.');
    } finally {
      setRegLoading(false);
    }
  }

  const inputCls = 'w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all text-textDark';

  return (
    <div className="max-w-md mx-auto px-4 py-12">
      {/* Tab switcher */}
      <div className="flex bg-gray-100 rounded-2xl p-1 mb-8">
        {[['login','Sign In'],['register','Create Account']].map(([val, label]) => (
          <button
            key={val}
            onClick={() => { setMode(val); setLoginError(''); setRegError(''); }}
            className={`flex-1 py-3 rounded-xl font-bold text-sm transition-all ${mode === val ? 'bg-white text-primary shadow-sm' : 'text-textLight hover:text-textDark'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {mode === 'login' ? (
        <div className="bg-white rounded-[32px] p-8 shadow-sm border border-gray-100">
          <h2 className="text-2xl font-black text-textDark mb-1 italic">Welcome Back</h2>
          <p className="text-textLight text-sm mb-6 font-bold">Sign in to access your account.</p>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Email or Username</label>
              <input
                type="text"
                value={loginForm.username}
                onChange={e => setLoginForm(f => ({ ...f, username: e.target.value }))}
                required
                autoComplete="username"
                placeholder="you@example.com"
                className={inputCls}
              />
            </div>
            <div className="relative">
              <label className="block text-sm font-bold text-gray-700 mb-2">Password</label>
              <input
                type={showLoginPwd ? 'text' : 'password'}
                value={loginForm.password}
                onChange={e => setLoginForm(f => ({ ...f, password: e.target.value }))}
                required
                autoComplete="current-password"
                placeholder="••••••••"
                className={`${inputCls} pr-12`}
              />
              <button type="button" onClick={() => setShowLoginPwd(s => !s)} className="absolute right-4 top-10 text-gray-400 hover:text-gray-600">
                {showLoginPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm font-bold text-textLight cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={loginForm.remember}
                  onChange={e => setLoginForm(f => ({ ...f, remember: e.target.checked }))}
                  className="rounded border-gray-300 text-primary focus:ring-primary w-4 h-4"
                />
                Remember me
              </label>
              <a href={`${siteUrl}/my-account/lost-password/`} className="text-sm font-bold text-primary hover:text-primaryHover">
                Forgot password?
              </a>
            </div>
            {loginError && <p className="text-red-500 text-sm font-bold bg-red-50 px-4 py-3 rounded-xl">{loginError}</p>}
            <button
              type="submit"
              disabled={loginLoading}
              className="w-full bg-primary hover:bg-primaryHover text-white font-black py-4 rounded-2xl transition-colors disabled:opacity-60 text-sm mt-2"
            >
              {loginLoading ? 'Signing In…' : 'Sign In'}
            </button>
          </form>
        </div>
      ) : (
        <div className="bg-white rounded-[32px] p-8 shadow-sm border border-gray-100">
          <h2 className="text-2xl font-black text-textDark mb-1 italic">Create Account</h2>
          <p className="text-textLight text-sm mb-6 font-bold">Join us for a great shopping experience.</p>
          <form onSubmit={handleRegister} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">First Name</label>
                <input
                  type="text"
                  value={regForm.firstName}
                  onChange={e => setRegForm(f => ({ ...f, firstName: e.target.value }))}
                  required
                  autoComplete="given-name"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Last Name</label>
                <input
                  type="text"
                  value={regForm.lastName}
                  onChange={e => setRegForm(f => ({ ...f, lastName: e.target.value }))}
                  autoComplete="family-name"
                  className={inputCls}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Email Address</label>
              <input
                type="email"
                value={regForm.email}
                onChange={e => setRegForm(f => ({ ...f, email: e.target.value }))}
                required
                autoComplete="email"
                placeholder="you@example.com"
                className={inputCls}
              />
            </div>
            <div className="relative">
              <label className="block text-sm font-bold text-gray-700 mb-2">Password</label>
              <input
                type={showRegPwd ? 'text' : 'password'}
                value={regForm.password}
                onChange={e => setRegForm(f => ({ ...f, password: e.target.value }))}
                required
                minLength={6}
                autoComplete="new-password"
                placeholder="Min. 6 characters"
                className={`${inputCls} pr-12`}
              />
              <button type="button" onClick={() => setShowRegPwd(s => !s)} className="absolute right-4 top-10 text-gray-400 hover:text-gray-600">
                {showRegPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {regError && <p className="text-red-500 text-sm font-bold bg-red-50 px-4 py-3 rounded-xl">{regError}</p>}
            <button
              type="submit"
              disabled={regLoading}
              className="w-full bg-primary hover:bg-primaryHover text-white font-black py-4 rounded-2xl transition-colors disabled:opacity-60 text-sm mt-2"
            >
              {regLoading ? 'Creating Account…' : 'Create Account'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────
export default function AccountPage() {
  const [tab,     setTab]     = useState(getTab);
  const [user,    setUser]    = useState(null);
  const [orders,  setOrders]  = useState([]);
  const [loading, setLoading] = useState(true);

  const isLoggedIn = !!(window.themeData?.isLoggedIn);

  useEffect(() => {
    const h = () => setTab(getTab());
    window.addEventListener('popstate', h);
    return () => window.removeEventListener('popstate', h);
  }, []);

  useEffect(() => {
    if (!isLoggedIn) { setLoading(false); return; }
    Promise.all([
      apiFetch('/account').then(setUser).catch(() => {}),
      apiFetch('/account/orders?per_page=20').then(setOrders).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  function onNav(newTab) {
    setTab(newTab);
    const paths = { dashboard: '', orders: 'orders/', addresses: 'edit-address/', wishlist: 'wishlist/', profile: 'edit-account/' };
    navTo(paths[newTab] ?? '');
  }

  function onViewOrder(id) {
    setTab(`order-${id}`);
    navTo(`view-order/${id}/`);
  }

  if (tab === 'forgot-password') {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <TabForgotPassword />
      </div>
    );
  }

  if (tab === 'reset-password') {
    const q = new URLSearchParams(window.location.search);
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <TabResetPassword resetKey={q.get('key')} login={q.get('login')} />
      </div>
    );
  }

  if (!isLoggedIn) {
    return <LoginRegisterPanel />;
  }

  const activeOrderId = tab.startsWith('order-') ? parseInt(tab.replace('order-', ''), 10) : null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 pt-8">
      <div className="flex flex-col lg:flex-row gap-8">
        <Sidebar user={user} activeTab={tab} onNav={onNav} />

        <div className="flex-grow min-w-0">
          {(tab === 'dashboard' || tab === 'profile') && (
            <TabDashboard user={user} setUser={setUser} orders={orders} loading={loading} onViewOrder={onViewOrder} onNav={onNav} />
          )}
          {tab === 'orders' && (
            <TabOrders orders={orders} loading={loading} onViewOrder={onViewOrder} />
          )}
          {activeOrderId && (
            <TabOrderDetail orderId={activeOrderId} onBack={() => onNav('orders')} />
          )}
          {tab === 'addresses' && <TabAddresses user={user} setUser={setUser} />}
          {tab === 'wishlist'  && <TabWishlist />}
        </div>
      </div>
    </div>
  );
}
